import React, { useState, useEffect } from 'react';
import { reportService, ReportFilters } from '../../services/reportService';
import { PDFGenerator } from '../../utils/pdfGenerator';
import { toast } from 'react-hot-toast';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

interface Props {
    filters: ReportFilters;
    onPreview: (url: string, title: string) => void;
}

type ReportType = 'sales' | 'preorders' | 'layaways' | 'invoices' | 'payments' | 'commissions';

const safeFormatDate = (dateVal: any, formatStr: string) => {
    if (!dateVal) return '-';
    try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return String(dateVal);
        return format(d, formatStr);
    } catch {
        return String(dateVal);
    }
};

export default function OperationalReports({ filters, onPreview }: Props) {
    const [activeReport, setActiveReport] = useState<ReportType>('sales');
    const [reportData, setReportData] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>({});
    const [isLoading, setIsLoading] = useState(false);
    
    // Grouped commissions for easier rendering
    const groupedData = React.useMemo(() => {
        if (activeReport !== 'commissions' || !reportData.length) return null;

        const groups: any = {};
        
        reportData.forEach(row => {
            const v = row.vendedor || 'Sin Vendedor';
            const rDate = new Date(row.reportingDate || row.fecha);
            
            // Format Month Key: "Marzo 2026"
            const monthName = format(rDate, 'MMMM yyyy', { locale: es });
            
            // Format Week Key: "02 Mar - 08 Mar"
            const weekStart = startOfWeek(rDate, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(rDate, { weekStartsOn: 1 });
            const weekRange = `${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`;

            if (!groups[v]) groups[v] = { name: v, total: 0, months: {} };
            if (!groups[v].months[monthName]) groups[v].months[monthName] = { name: monthName, total: 0, weeks: {} };
            if (!groups[v].months[monthName].weeks[weekRange]) groups[v].months[monthName].weeks[weekRange] = { range: weekRange, total: 0, items: [] };

            groups[v].total += row.montoComision;
            groups[v].months[monthName].total += row.montoComision;
            groups[v].months[monthName].weeks[weekRange].total += row.montoComision;
            groups[v].months[monthName].weeks[weekRange].items.push(row);
        });

        return groups;
    }, [reportData, activeReport]);

    const [invoiceViewMode, setInvoiceViewMode] = useState<'summary' | 'detailed'>('summary');

    useEffect(() => {
        loadData();
    }, [filters, activeReport, invoiceViewMode]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            let res: any;
            switch (activeReport) {
                case 'sales': res = await reportService.getReportSales(filters); break;
                case 'preorders': res = await reportService.getReportPreOrders(filters); break;
                case 'layaways': res = await reportService.getReportLayaways(filters); break;
                case 'invoices': res = await reportService.getReportInvoices(filters); break;
                case 'payments': res = await reportService.getReportPayments(filters); break;
                case 'commissions': res = await reportService.getReportCommissions(filters); break;
            }
            if (res?.success) {
                setReportData(res.data);
                if (res.summary) setSummary(res.summary);
            }
        } catch (error) {
            toast.error('Error al cargar datos del reporte');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGeneratePDF = () => { // Renamed from handleExport
        if (!reportData || reportData.length === 0) {
            toast.error('No hay datos para exportar');
            return;
        }

        const period = {
            start: new Date(filters.startDate + 'T00:00:00'),
            end: new Date(filters.endDate + 'T23:59:59')
        };

        const gen = new PDFGenerator('l');
        
        // Define columns based on active report
        let cols: string[] = [];
        let body: any[][] = [];
        let title = '';

        if (activeReport === 'sales') {
            title = 'Reporte de Ventas';
            cols = ['Fecha', 'Folio', 'Cliente', 'Marca', 'Modelo', 'Talla', 'Color', 'Cant', 'Total', 'Vendedor', 'Estado'];
            body = reportData.map(r => [
                safeFormatDate(r.fecha, 'dd/MM/yyyy'),
                r.folio,
                r.cliente,
                r.marca,
                r.modelo,
                r.talla,
                r.color,
                r.cantidad,
                `$${r.total.toFixed(2)}`,
                r.vendedor || 'N/A',
                r.estado
            ]);
        } else if (activeReport === 'preorders' || activeReport === 'layaways') {
            title = activeReport === 'preorders' ? 'Reporte de Pre-Orders' : 'Reporte de Apartados';
            cols = ['Fecha', 'Folio', 'Cliente', 'Modelo', 'Talla', 'Color', 'Cant', 'Total', 'Abonado', 'Pendiente', 'Llegada', 'Vendedor', 'Estado'];
            body = reportData.map(r => [
                safeFormatDate(r.fecha, 'dd/MM/yyyy'),
                r.folio,
                r.cliente,
                r.modelo,
                r.talla,
                r.color,
                r.cantidad,
                `$${r.total.toFixed(2)}`,
                `$${(r.abonado || 0).toFixed(2)}`,
                `$${(r.pendiente || 0).toFixed(2)}`,
                r.llegada || 'PENDIENTE',
                r.vendedor || 'N/A',
                r.estado
            ]);
        } else if (activeReport === 'payments') {
            title = 'Reporte de Pagos / Abonos';
            cols = ['Fecha', 'Folio Venta', 'Cliente', 'Método', 'Tipo Oper.', 'Monto', 'Restante', 'Cajero'];
            body = reportData.map(p => [
                safeFormatDate(p.fecha, 'dd/MM/yyyy'),
                p.folioVenta,
                p.cliente,
                p.metodo,
                p.tipo,
                `$${Number(p.monto).toFixed(2)}`,
                `$${Number(p.restanteActual).toFixed(2)}`,
                p.cajero
            ]);
        } else if (activeReport === 'invoices') {
            title = invoiceViewMode === 'summary' ? 'Reporte Resumido de Invoices' : 'Reporte Detallado de Invoices';
            
            if (invoiceViewMode === 'summary') {
                cols = ['Fecha', 'Folio', 'Total Items', 'Estado'];
                body = reportData.map(i => [
                    i.fecha,
                    i.folio,
                    i.totalItems,
                    i.estado
                ]);
            } else {
                cols = ['', 'Marca', 'Modelo', 'Talla', 'Color', 'Precio', 'Estado'];
                body = [];
                reportData.forEach(inv => {
                    body.push([{ content: `Invoice: ${inv.folio} | Fecha: ${inv.fecha} | Total: ${inv.totalItems} | Estado: ${inv.estado}`, colSpan: 7, styles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [50, 50, 50] } }]);
                    if (inv.items) {
                        inv.items.forEach((item: any) => {
                            body.push([
                                '',
                                item.marca,
                                item.modelo,
                                item.talla,
                                item.color,
                                `$${Number(item.precio).toFixed(2)}`,
                                item.estado
                            ]);
                        });
                    }
                });
            }
        } else if (activeReport === 'commissions') {
            title = 'Reporte Estructurado de Comisiones (Vendedora > Mes > Semana)';
            cols = ['', 'Marca', 'Modelo', 'Talla', 'Color', 'Precio', 'Tipo'];
            body = [];

            if (groupedData) {
                Object.values(groupedData).forEach((seller: any) => {
                    // Seller Header
                    body.push([{ 
                        content: `VENDEDORA: ${seller.name.toUpperCase()} | TOTAL PERIODO: $${seller.total.toLocaleString()}`, 
                        colSpan: 7, 
                        styles: { fillColor: [40, 44, 52], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11 } 
                    }]);

                    Object.values(seller.months).forEach((month: any) => {
                        // Month Sub-header
                        body.push([{ 
                            content: `MES: ${month.name.toUpperCase()} | SUBTOTAL MES: $${month.total.toLocaleString()}`, 
                            colSpan: 7, 
                            styles: { fillColor: [79, 70, 229], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 } 
                        }]);

                        Object.values(month.weeks).forEach((week: any) => {
                            // Week Sub-header
                            body.push([{ 
                                content: `SEMANA: ${week.range} | TOTAL SEMANA: $${week.total.toLocaleString()}`, 
                                colSpan: 7, 
                                styles: { fillColor: [243, 244, 246], textColor: [50, 50, 50], fontStyle: 'bold', fontSize: 9 } 
                            }]);

                            week.items.forEach((row: any) => {
                                // Payment Header (thin grey)
                                body.push([{ 
                                    content: `   ↳ Pago: ${row.folioPago} | Fecha: ${safeFormatDate(row.fecha, 'dd/MM/yyyy')} | Cliente: ${row.cliente} | Monto: $${row.montoPago.toLocaleString()} | Comis: $${row.montoComision.toLocaleString()} (${row.porcentaje}%)`, 
                                    colSpan: 7, 
                                    styles: { fillColor: [250, 250, 250], textColor: [80, 80, 80], fontStyle: 'italic', fontSize: 8 } 
                                }]);

                                if (row.prendas) {
                                    row.prendas.forEach((p: any) => {
                                        body.push([
                                            '',
                                            p.marca,
                                            p.modelo,
                                            p.talla,
                                            p.color,
                                            `$${Number(p.precio).toFixed(2)}`,
                                            p.tipo
                                        ]);
                                    });
                                }
                            });
                        });
                    });
                });
            }
        }

        gen.generateTable(cols, body, title, period);
        onPreview(gen.getBlobUrl(), title);
    };

    const tabs = [
        { id: 'sales', name: 'Ventas Detalladas' },
        { id: 'preorders', name: 'Pre-Orders' },
        { id: 'layaways', name: 'Apartados' },
        { id: 'invoices', name: 'Invoices (Compras)' },
        { id: 'payments', name: 'Pagos Recibidos' },
        { id: 'commissions', name: 'Comisiones' }
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveReport(tab.id as ReportType)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeReport === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                    >
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* Summary Cards */}
            {summary && Object.keys(summary).length > 0 && activeReport === 'sales' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <p className="text-sm text-gray-500 font-medium tracking-wide">Transacciones</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalVentas}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <p className="text-sm text-gray-500 font-medium tracking-wide">Ingresos Cobrados</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">${summary.totalIngresos?.toLocaleString()}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <p className="text-sm text-gray-500 font-medium tracking-wide">Saldo Pendiente</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">${summary.totalPendiente?.toLocaleString()}</p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-5 bg-gray-50 border-b border-gray-100">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {activeReport === 'sales' ? 'Registro de Ventas' :
                            activeReport === 'preorders' ? 'Registro de Pre-Orders' :
                                activeReport === 'layaways' ? 'Registro de Apartados' :
                                    activeReport === 'payments' ? 'Registro de Pagos' :
                                        activeReport === 'commissions' ? 'Registro de Comisiones' :
                                            'Registro de Invoices (Entradas)'}
                    </h2>
                    <div className="flex space-x-3 items-center">
                        {activeReport === 'invoices' && (
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setInvoiceViewMode('summary')}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${invoiceViewMode === 'summary' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                >
                                    Resumen
                                </button>
                                <button
                                    onClick={() => setInvoiceViewMode('detailed')}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${invoiceViewMode === 'detailed' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                >
                                    Detallado
                                </button>
                            </div>
                        )}
                        <button
                            onClick={handleGeneratePDF}
                            disabled={reportData.length === 0}
                            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                        <DocumentArrowDownIcon className="w-5 h-5" />
                        Generar PDF Exportable
                    </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto max-h-[500px]">
                    {isLoading ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="animate-spin w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full mb-4"></div>
                            Cargando datos...
                        </div>
                    ) : reportData.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            No se encontraron datos para los filtros seleccionados.
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left whitespace-nowrap">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-100 sticky top-0 z-10 shadow-sm">
                                {activeReport === 'sales' ? (
                                    <tr>
                                        <th className="px-4 py-3">Fecha</th>
                                        <th className="px-4 py-3">Folio</th>
                                        <th className="px-4 py-3">Marca</th>
                                        <th className="px-4 py-3">Modelo</th>
                                        <th className="px-4 py-3">Talla</th>
                                        <th className="px-4 py-3">Color</th>
                                        <th className="px-4 py-3 text-right">Cant.</th>
                                        <th className="px-4 py-3 text-right">Total</th>
                                        <th className="px-4 py-3">Vendedora</th>
                                        <th className="px-4 py-3">Estado</th>
                                    </tr>
                                ) : (activeReport === 'preorders' || activeReport === 'layaways') ? (
                                    <tr>
                                        <th className="px-4 py-3">Fecha</th>
                                        <th className="px-4 py-3">Folio</th>
                                        <th className="px-4 py-3">Marca</th>
                                        <th className="px-4 py-3">Modelo</th>
                                        <th className="px-4 py-3">Talla</th>
                                        <th className="px-4 py-3">Color</th>
                                        <th className="px-4 py-3 text-right">Total</th>
                                        <th className="px-4 py-3 text-right text-blue-600">Abonado</th>
                                        <th className="px-4 py-3 text-right text-orange-600">Pendiente</th>
                                        <th className="px-4 py-3 text-center">Llegada</th>
                                        <th className="px-4 py-3">Vendedora</th>
                                        <th className="px-4 py-3">Estado</th>
                                    </tr>
                                ) : activeReport === 'payments' ? (
                                    <tr>
                                        <th className="px-4 py-3">Fecha</th>
                                        <th className="px-4 py-3">Folio Venta</th>
                                        <th className="px-4 py-3">Cliente</th>
                                        <th className="px-4 py-3">Método</th>
                                        <th className="px-4 py-3">Operación</th>
                                        <th className="px-4 py-3 text-right">Monto</th>
                                        <th className="px-4 py-3 text-right">Restante</th>
                                        <th className="px-4 py-3">Cajero</th>
                                    </tr>
                                ) : activeReport === 'commissions' ? (
                                    <tr>
                                        <th className="px-4 py-3 border-r border-gray-200">Ref.</th>
                                        <th className="px-4 py-3 text-left">Marca</th>
                                        <th className="px-4 py-3">Modelo</th>
                                        <th className="px-4 py-3 text-center">Talla</th>
                                        <th className="px-4 py-3 text-center">Color</th>
                                        <th className="px-4 py-3 text-right">Precio</th>
                                        <th className="px-4 py-3 text-center">Estado/Tipo</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        {invoiceViewMode === 'summary' ? (
                                            <>
                                                <th className="px-4 py-3">Fecha</th>
                                                <th className="px-4 py-3">Folio Invoice</th>
                                                <th className="px-4 py-3 text-right">Total Items</th>
                                                <th className="px-4 py-3">Estado</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="px-4 py-3 border-r border-gray-200">Ref.</th>
                                                <th className="px-4 py-3">Marca</th>
                                                <th className="px-4 py-3">Modelo</th>
                                                <th className="px-4 py-3">Talla</th>
                                                <th className="px-4 py-3">Color</th>
                                                <th className="px-4 py-3 text-right">Precio</th>
                                                <th className="px-4 py-3">Estado</th>
                                            </>
                                        )}
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                        {reportData.length > 0 ? (
                                            activeReport === 'invoices' ? (
                                                reportData.map((inv, idx) => (
                                                    <React.Fragment key={idx}>
                                                        {invoiceViewMode === 'summary' ? (
                                                            <tr className="hover:bg-gray-50 border-b border-gray-100 text-sm">
                                                                <td className="px-4 py-3 text-gray-600">{inv.fecha}</td>
                                                                <td className="px-4 py-3 font-medium text-indigo-900">{inv.folio}</td>
                                                                <td className="px-4 py-3 text-right font-medium">{inv.totalItems}</td>
                                                                <td className="px-4 py-3"><span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">{inv.estado}</span></td>
                                                            </tr>
                                                        ) : (
                                                            <>
                                                                <tr className="bg-gray-100 border-b border-gray-200">
                                                                    <td colSpan={7} className="px-4 py-3">
                                                                        <div className="flex space-x-6 font-semibold text-gray-800 text-sm">
                                                                            <span>Invoice: <span className="font-bold text-indigo-900">{inv.folio}</span></span>
                                                                            <span>Fecha: {inv.fecha}</span>
                                                                            <span>Total Items: {inv.totalItems}</span>
                                                                            <span className="text-indigo-600">{inv.estado}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                {inv.items && inv.items.map((item: any, iidx: number) => (
                                                                    <tr key={`${idx}-${iidx}`} className="hover:bg-gray-50 border-b border-gray-100 text-sm">
                                                                        <td className="px-4 py-2 pl-6 text-gray-400 border-r border-gray-100 text-center font-mono">↳</td>
                                                                        <td className="px-4 py-2 font-medium text-gray-900">{item.marca}</td>
                                                                        <td className="px-4 py-2 text-gray-700">{item.modelo}</td>
                                                                        <td className="px-4 py-2 text-gray-700">{item.talla}</td>
                                                                        <td className="px-4 py-2 text-gray-700">{item.color}</td>
                                                                        <td className="px-4 py-2 text-right font-medium text-gray-900">${item.precio?.toLocaleString()}</td>
                                                                        <td className="px-4 py-2"><span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">{item.estado}</span></td>
                                                                    </tr>
                                                                ))}
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            ) : activeReport === 'commissions' ? (
                                                groupedData && Object.values(groupedData).map((seller: any, sIdx: number) => (
                                                    <React.Fragment key={sIdx}>
                                                        {/* SELLER HEADER */}
                                                        <tr className="bg-indigo-900 border-b border-indigo-800">
                                                            <td colSpan={7} className="px-6 py-4">
                                                                <div className="flex justify-between items-center">
                                                                    <div className="flex items-center space-x-3">
                                                                        <div className="w-10 h-10 rounded-full bg-indigo-700 flex items-center justify-center text-white font-bold shadow-inner">
                                                                            {seller.name.charAt(0)}
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-[10px] text-indigo-300 font-black uppercase tracking-widest">Vendedora</p>
                                                                            <p className="text-lg font-bold text-white leading-tight">{seller.name}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className="text-[10px] text-indigo-300 font-black uppercase tracking-widest">Total Comisiones</p>
                                                                        <p className="text-2xl font-black text-indigo-100">${seller.total?.toLocaleString()}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {Object.values(seller.months).map((month: any, mIdx: number) => (
                                                            <React.Fragment key={`${sIdx}-${mIdx}`}>
                                                                {/* MONTH HEADER */}
                                                                <tr className="bg-indigo-600/10 border-b border-indigo-200/30">
                                                                    <td colSpan={7} className="px-6 py-2 bg-indigo-50/50">
                                                                        <div className="flex items-center justify-between text-indigo-900 font-bold text-sm tracking-tight">
                                                                            <span className="uppercase">{month.name}</span>
                                                                            <span className="text-[11px] font-black opacity-60">Subtotal Mes: ${month.total?.toLocaleString()}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>

                                                                {Object.values(month.weeks).map((week: any, wIdx: number) => (
                                                                    <React.Fragment key={`${sIdx}-${mIdx}-${wIdx}`}>
                                                                        {/* WEEK HEADER */}
                                                                        <tr className="bg-gray-50 border-b border-gray-100">
                                                                            <td colSpan={7} className="px-6 py-1.5 ">
                                                                                <div className="flex items-center justify-between text-[11px] font-black text-gray-400">
                                                                                    <div className="flex items-center space-x-2">
                                                                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                                                                                        <span className="uppercase tracking-widest">Semana: {week.range}</span>
                                                                                    </div>
                                                                                    <span className="text-gray-900">Total Semana: ${week.total?.toLocaleString()}</span>
                                                                                </div>
                                                                            </td>
                                                                        </tr>

                                                                        {week.items.map((row: any, idx: number) => (
                                                                            <React.Fragment key={`${sIdx}-${mIdx}-${wIdx}-${idx}`}>
                                                                                {/* PAYMENT ROW */}
                                                                                <tr className="bg-white border-b border-gray-100 group transition-colors">
                                                                                    <td colSpan={7} className="px-4 py-3 group-hover:bg-indigo-50/20">
                                                                                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 items-center">
                                                                                            <div className="flex flex-col">
                                                                                                <span className="text-[8px] uppercase text-gray-400 font-black">Folio</span>
                                                                                                <span className="text-xs font-bold text-indigo-700">{row.folioPago}</span>
                                                                                            </div>
                                                                                            <div className="flex flex-col">
                                                                                                <span className="text-[8px] uppercase text-gray-400 font-black">Fecha</span>
                                                                                                <span className="text-xs font-bold text-gray-700">{format(new Date(row.fecha), 'dd MMM yyyy', { locale: es })}</span>
                                                                                            </div>
                                                                                            <div className="flex flex-col col-span-1 lg:col-span-2">
                                                                                                <span className="text-[8px] uppercase text-gray-400 font-black">Cliente</span>
                                                                                                <span className="text-xs font-bold text-gray-700 truncate">{row.cliente}</span>
                                                                                            </div>
                                                                                            <div className="flex flex-col text-right">
                                                                                                <span className="text-[8px] uppercase text-gray-400 font-black">Pago Recibido</span>
                                                                                                <span className="text-xs font-black text-gray-900">${row.montoPago?.toLocaleString()}</span>
                                                                                            </div>
                                                                                            <div className="flex flex-col text-right">
                                                                                                <span className="text-[8px] uppercase text-gray-400 font-black">Comisión ({row.porcentaje}%)</span>
                                                                                                <span className="text-sm font-black text-green-600">${row.montoComision?.toLocaleString()}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>

                                                                                {/* ITEM ROWS */}
                                                                                {row.prendas && row.prendas.map((p: any, pidx: number) => (
                                                                                    <tr key={`${idx}-${pidx}`} className="hover:bg-gray-50/50 border-b border-gray-50 text-[11px] transition-colors">
                                                                                        <td className="px-4 py-1.5 text-center text-gray-300 font-mono border-r border-gray-50 w-10">↳</td>
                                                                                        <td className="px-4 py-1.5 font-bold text-gray-400 uppercase leading-none">{p.marca}</td>
                                                                                        <td className="px-4 py-1.5 font-bold text-indigo-900 leading-none">{p.modelo}</td>
                                                                                        <td className="px-4 py-1.5 font-black text-gray-500 text-center bg-gray-50/30">{p.talla}</td>
                                                                                        <td className="px-4 py-1.5 font-black text-gray-500 text-center">{p.color}</td>
                                                                                        <td className="px-4 py-1.5 text-right font-black text-gray-900">${p.precio?.toLocaleString()}</td>
                                                                                        <td className="px-4 py-1.5 text-center">
                                                                                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${p.tipo === 'PRE-ORDER' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                                                                                                {p.tipo}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </React.Fragment>
                                                                ))}
                                                            </React.Fragment>
                                                        ))}
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                reportData.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50 border-b border-gray-100 text-sm">
                                                        {activeReport === 'sales' ? (
                                                            <>
                                                                <td className="px-4 py-3 text-gray-600">{safeFormatDate(row.fecha, 'dd/MM/yyyy')}</td>
                                                                <td className="px-4 py-3 font-medium text-gray-900">{row.folio}</td>
                                                                <td className="px-4 py-3 font-semibold text-indigo-900">{row.marca}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.modelo}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.talla}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.color}</td>
                                                                <td className="px-4 py-3 text-right font-medium">{row.cantidad}</td>
                                                                <td className="px-4 py-3 text-right font-semibold text-green-700">${row.total?.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-gray-600">{row.vendedor || 'N/A'}</td>
                                                                <td className="px-4 py-3"><span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">{row.estado}</span></td>
                                                            </>
                                                        ) : (activeReport === 'preorders' || activeReport === 'layaways') ? (
                                                            <>
                                                                <td className="px-4 py-3 text-gray-600">{safeFormatDate(row.fecha, 'dd/MM/yyyy')}</td>
                                                                <td className="px-4 py-3 font-medium text-gray-900">{row.folio}</td>
                                                                <td className="px-4 py-3 font-semibold text-indigo-900">{row.marca}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.modelo}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.talla}</td>
                                                                <td className="px-4 py-3 text-gray-700">{row.color}</td>
                                                                <td className="px-4 py-3 text-right font-semibold">${row.total?.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-right font-bold text-blue-600">${(row.abonado || 0).toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-right font-black text-orange-600 bg-orange-50">${(row.pendiente || 0).toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${row.llegada === 'RECIBIDO' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-orange-100 text-orange-700 border border-orange-200'}`}>
                                                                        {row.llegada || 'PENDIENTE'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-gray-600">{row.vendedor || 'N/A'}</td>
                                                                <td className="px-4 py-3"><span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">{row.estado}</span></td>
                                                            </>
                                                        ) : activeReport === 'payments' ? (
                                                            <>
                                                                <td className="px-4 py-3 text-gray-600">{safeFormatDate(row.fecha, 'dd/MM/yyyy HH:mm')}</td>
                                                                <td className="px-4 py-3 font-medium">{row.folioVenta}</td>
                                                                <td className="px-4 py-3">{row.cliente}</td>
                                                                <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{row.metodo}</span></td>
                                                                <td className="px-4 py-3"><span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{row.tipo}</span></td>
                                                                <td className="px-4 py-3 text-right font-semibold text-green-700">${row.monto?.toLocaleString()}</td>
                                                                <td className="px-4 py-3 text-right font-medium text-orange-600">${row.restanteActual?.toLocaleString()}</td>
                                                                <td className="px-4 py-3">{row.cajero}</td>
                                                            </>
                                                        ) : null}
                                                    </tr>
                                                ))
                                            )
                                        ) : (
                                            <tr>
                                                <td colSpan={10} className="px-4 py-12 text-center text-gray-500 italic">
                                                    No se encontraron registros para los filtros seleccionados.
                                                </td>
                                            </tr>
                                        )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
